/**
 * AIHomeworkFeedbackHelper Component - AI assistant for homework feedback
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sparkles, Loader2, Copy, Check, Wand2 } from 'lucide-react';
import { useAIGeneration, getErrorMessage, safeString } from './utils';

export const AIHomeworkFeedbackHelper = ({ homeworkTitle, homeworkDescription, studentName, onInsert }) => {
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('constructive');
  
  const { loading, generatedText, setGeneratedText, copied, generate, handleCopy } = useAIGeneration('homework-feedback');
  
  const safeTitle = safeString(homeworkTitle, 'Homework');
  const safeDescription = safeString(homeworkDescription, '');
  const safeStudent = safeString(studentName, 'Student');
  
  const handleGenerate = async () => {
    try {
      const response = await generate({
        homework_title: safeTitle,
        homework_description: safeDescription || null,
        student_name: safeStudent,
        feedback_type: feedbackType
      });
      setGeneratedText(response.generated_text);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate feedback'));
    }
  };
  
  const handleInsert = () => {
    if (onInsert) onInsert(generatedText);
    setOpen(false);
    setGeneratedText('');
    toast.success('Feedback inserted');
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          AI Feedback
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-500" />
            AI Feedback Generator
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <Card className="bg-slate-100">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600 mb-1">Generating feedback for:</p>
              <p className="font-medium">{safeTitle}</p>
              <p className="text-sm text-gray-500">Submitted by {safeStudent}</p>
            </CardContent>
          </Card>
          
          <div>
            <Label>Feedback Style</Label>
            <Select value={feedbackType} onValueChange={setFeedbackType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="constructive">Constructive & Balanced</SelectItem>
                <SelectItem value="encouraging">Encouraging & Supportive</SelectItem>
                <SelectItem value="detailed">Detailed & Thorough</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button 
            onClick={handleGenerate} 
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate Feedback</>
            )}
          </Button>
          
          {generatedText && (
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm text-gray-600">Generated Feedback:</Label>
                  <Button variant="ghost" size="sm" onClick={() => handleCopy(generatedText)}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="whitespace-pre-wrap text-sm bg-white p-3 rounded border max-h-60 overflow-y-auto">
                  {generatedText}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        {generatedText && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGeneratedText('')}>
              Regenerate
            </Button>
            <Button onClick={handleInsert} className="bg-cyan-600 hover:bg-cyan-700">
              Use This Feedback
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
